import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { 
  AlertCircle, 
  PoundSterling, 
  Clock, 
  CheckCircle2, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Building2,
  FileText,
  Calendar
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { DashboardSelector } from "@/components/dashboard"
import { UserFilter } from "@/components/filters"
import { useDisputesMetrics, useCanViewAllDisputes } from "@/hooks/use-disputes-metrics"
import { useAuth } from "@/hooks/use-auth"
import { useFilter } from "@/contexts/filter-context"
import { useEffect } from "react"
import { DataModeBadge } from "@/components/DataModeBadge"

const getPriorityBadgeVariant = (priority: string): { variant?: "default" | "secondary" | "destructive" | "outline" | "warning"; accent?: "teal" | "purple" | "info" | "success" | "warning" | "destructive" | "nile" | "cerulean" | "lagoon" | "eagle" | "sap" | "orange" | "brick" | "red" | "violet" | "blue-violet" } => {
  switch (priority) {
    case "Critical": return { variant: "destructive" }
    case "High": return { variant: "warning", accent: "orange" }
    case "Medium": return { variant: "default" }
    case "Low": return { variant: "secondary" }
    default: return { variant: "default" }
  }
}

const getPriorityBorderColor = (priority: string) => {
  switch (priority) {
    case "Critical": return "rgb(239, 68, 68)"
    case "High": return "rgb(249, 115, 22)"
    case "Medium": return "rgb(148, 163, 184)"
    case "Low": return "rgb(156, 163, 175)"
    default: return "rgb(156, 163, 175)"
  }
}

export default function DisputesDashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canViewAll = useCanViewAllDisputes()
  const { currentFilter, setCurrentFilter } = useFilter()
  
  // Get metrics with role-based filtering
  const metrics = useDisputesMetrics(currentFilter)
  
  // Log filter synchronization
  useEffect(() => {
    console.log('[DisputesDashboard] Filter synchronized from context:', currentFilter)
  }, [currentFilter])
  
  console.log('[DisputesDashboard] Rendering with metrics:', {
    activeCount: metrics.activeCount,
    totalAmount: metrics.totalAmount,
    isFiltered: metrics.isFiltered,
    filterType: metrics.filterType,
    userName: metrics.userName
  })

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Dashboard Toggle Buttons */}
      <div className="flex items-center justify-between">
        <DashboardSelector />
        
        {/* Filter Dropdown - Only for managers */}
        {canViewAll && (
          <UserFilter 
            value={currentFilter} 
            onValueChange={setCurrentFilter}
          />
        )}
      </div>

      {/* Header Section */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">
              Dispute Dashboard
            {metrics.isFiltered && (
              <span className="text-lg font-normal text-muted-foreground ml-2">
                - {metrics.userName}
              </span>
            )}
            </h1>
            <DataModeBadge />
          </div>
          <p className="text-muted-foreground mt-1">
            {metrics.isFiltered 
              ? `Monitor ${metrics.userName}'s dispute metrics and track resolution progress`
              : 'Monitor team dispute metrics and track resolution progress'
            }
          </p>
        </div>
        <Button 
          onClick={() => navigate("/disputes/all")}
          className="bg-teal-600 hover:bg-teal-700"
        >
          View All Disputes
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Metrics Cards Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Active Disputes */}
        <Card className="border-l-4 border-l-accent-orange-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Disputes</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeCount}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingDown className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">-12%</span>
              <span className="ml-1">vs last week</span>
            </div>
          </CardContent>
        </Card>

        {/* Amount in Dispute */}
        <Card className="border-l-4 border-l-accent-destructive-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Amount in Dispute</CardTitle>
            <PoundSterling className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              £{(metrics.totalAmount / 1000000).toFixed(2)}M
            </div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingUp className="mr-1 h-3 w-3 text-red-600" />
              <span className="text-red-600 font-medium">+8%</span>
              <span className="ml-1">vs last week</span>
            </div>
          </CardContent>
        </Card>

        {/* Avg Resolution Time */}
        <Card className="border-l-4 border-l-accent-info-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Resolution Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.avgResolutionTime} days</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingDown className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">-18%</span>
              <span className="ml-1">vs last week</span>
            </div>
          </CardContent>
        </Card>

        {/* Resolution Rate */}
        <Card className="border-l-4 border-l-accent-success-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolution Rate</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.resolutionRate}%</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingUp className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">+5.2%</span>
              <span className="ml-1">vs last week</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two-Column Mid Section */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Disputes by Priority */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Disputes by Priority</CardTitle>
            <CardDescription>Current distribution across priority levels</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="destructive">Critical</Badge>
                  <span className="text-sm text-muted-foreground">
                    {metrics.byPriority.critical.count} disputes
                  </span>
                </div>
                <span className="text-sm font-medium">
                  £{(metrics.byPriority.critical.amount / 1000).toFixed(0)}K
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="warning" accent="orange">High</Badge>
                  <span className="text-sm text-muted-foreground">
                    {metrics.byPriority.high.count} disputes
                  </span>
                </div>
                <span className="text-sm font-medium">
                  £{(metrics.byPriority.high.amount / 1000).toFixed(0)}K
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="default">Medium</Badge>
                  <span className="text-sm text-muted-foreground">
                    {metrics.byPriority.medium.count} disputes
                  </span>
                </div>
                <span className="text-sm font-medium">
                  £{(metrics.byPriority.medium.amount / 1000).toFixed(0)}K
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="secondary">Low</Badge>
                  <span className="text-sm text-muted-foreground">
                    {metrics.byPriority.low.count} disputes
                  </span>
                </div>
                <span className="text-sm font-medium">
                  £{(metrics.byPriority.low.amount / 1000).toFixed(0)}K
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Common Dispute Reasons */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Common Dispute Reasons</CardTitle>
            <CardDescription>Top reasons for customer disputes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {metrics.byReason.map((item) => (
                <div key={item.reason} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{item.reason}</span>
                    <span className="text-muted-foreground">{item.count}</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-600 rounded-full"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Dispute Activity Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Recent Dispute Activity</CardTitle>
          <CardDescription>
            {metrics.recentDisputes.length > 0 
              ? 'Latest disputes requiring attention'
              : 'No active disputes requiring attention'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {metrics.recentDisputes.length > 0 ? (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {metrics.recentDisputes.map((dispute) => (
                  <Card
                    key={dispute.id}
                    className="border-l-4"
                    style={{
                      borderLeftColor: getPriorityBorderColor(
                        dispute.priority.charAt(0).toUpperCase() + dispute.priority.slice(1)
                      )
                    }}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{dispute.id}</span>
                          <Badge {...getPriorityBadgeVariant(
                            dispute.priority.charAt(0).toUpperCase() + dispute.priority.slice(1)
                          )}>
                            {dispute.priority.charAt(0).toUpperCase() + dispute.priority.slice(1)}
                          </Badge>
                        </div>
                        <span className="text-lg font-bold">
                          £{dispute.amount.toLocaleString()}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Building2 className="h-4 w-4" />
                          <span>{dispute.company}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <FileText className="h-4 w-4" />
                            <span>{dispute.reason}</span>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            <span>{dispute.daysOpen} days open</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-4"
                        onClick={() => navigate("/disputes/all")}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600 mb-4" />
              <p className="text-lg font-medium">All Clear!</p>
              <p className="text-sm text-muted-foreground mt-1">
                No disputes requiring attention at this time.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
